import React, {useState} from 'react';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';

import ApproveAskPopup from '../../../Components/ApproveAskPopup/ApproveAskPopup';
import {
	useAcceptProcessedOrderMutation,
	useApprovePayOrderMutation,
	useDeleteLogisticOrderMutation,
	useShipOrderMutation,
} from '../../../redux/DepotSlice/depotLogisticApi';

import edit from '../../../assets/img/icons/customerOrder/el_pencil-alt.svg';
import close from '../../../assets/img/icons/customerOrder/gridicons_cross-circle.svg';
import profile from '../../../assets/img/icons/customerOrder/solar_user-cross-rounded-bold.svg';
import approve from '../../../assets/img/icons/customerOrder/carbon_checkmark-filled.svg';
import ship from '../../../assets/img/icons/customerOrder/solar_delivery-bold.svg';
import info from '../../../assets/img/icons/customerOrder/info 1.svg';

import PopupStatus from '../../MyOrders/MyOrdersPopups/PopupStatus';
import {useLazyGetStoreOrderInfoQuery} from '../../../redux/DepotSlice/depotOrdersApi';
import {
	handleApiCall,
	adaptStoreOrderToPopupStatus,
} from '../../../assets/utils/utils';

type Props = {
	orderId: number;
	orderStatusPay: boolean;
	orderStatus: string;
	onDeleteSuccess: (orderId: number) => void;
};

type ActionType =
	| 'edit'
	| 'delete'
	| 'acceptProcessing'
	| 'approvePay'
	| 'ship'
	| 'info';

type ActionConfig = {
	type: ActionType;
	icon: string;
	info: string;
	confirmText?: string;
	requiresConfirmation?: boolean;
	disabled?: (orderStatus: string, orderStatusPay: boolean) => boolean;
	handler: (orderId: number) => Promise<void> | void;
};

const CustomerOrderAction: React.FC<Props> = ({
	orderId,
	orderStatus,
	orderStatusPay,
	onDeleteSuccess,
}) => {
	const {t} = useTranslation();
	const [activeConfirm, setActiveConfirm] = useState<ActionType | null>(null);
	const [openInfoPopup, setOpenInfoPopup] = useState<boolean>(false);

	const [deleteOrder] = useDeleteLogisticOrderMutation();
	const [acceptProcessedOrder] = useAcceptProcessedOrderMutation();
	const [approvePayOrder] = useApprovePayOrderMutation();
	const [shipOrder] = useShipOrderMutation();
	const [trigger, {data: orderInfo}] = useLazyGetStoreOrderInfoQuery();
	// === HANDLERS ===
	const handlers = {
		edit: () => void toast.error(t('Редагування не доступне')),
		delete: async () => {
			await deleteOrder({order_id: orderId, type_reject: 'ware'}).unwrap();
			onDeleteSuccess(orderId);
			toast.success(t('Успішно'));
		},

		acceptProcessing: async () => {
			await acceptProcessedOrder(orderId).unwrap();
			toast.success(t('Успішно'));
		},
		approvePay: async () => {
			await approvePayOrder(orderId).unwrap();
			toast.success(t('Успішно'));
		},
		ship: async () => {
			await shipOrder(orderId).unwrap();
			toast.success(t('Успішно'));
		},
		info: async () => {
			await handleApiCall({
				apiCall: () => trigger(orderId),
				onSuccess: () => {
					setOpenInfoPopup(true);
				},
			});
		},
	};

	const handleClosePopup = () => {
		setOpenInfoPopup(false);
	};

	const actions: ActionConfig[] = [
		{
			type: 'edit',
			icon: edit,
			info: t('Редагувати'),
			handler: handlers.edit,
		},
		{
			type: 'delete',
			icon: close,
			info: t('Видалити'),
			requiresConfirmation: true,
			confirmText: t('Ви дійсно хочете видалити замовлення?'),
			disabled: (status) => status === 'confirmed' || status === 'created',
			handler: handlers.delete,
		},
		{
			type: 'acceptProcessing',
			icon: approve,
			info: t('Прийняти в обробку'),
			requiresConfirmation: true,
			confirmText: t('Ви дійсно хочете прийняти замовлення в обробку?'),
			handler: handlers.acceptProcessing,
		},
		{
			type: 'approvePay',
			icon: profile,
			info: t('Підтвердити оплату'),
			requiresConfirmation: true,
			confirmText: t('Ви дійсно хочете підтвердити оплату замовлення?'),
			disabled: (_, pay) => !pay && orderStatus !== 'rejected',
			handler: handlers.approvePay,
		},
		{
			type: 'ship',
			icon: ship,
			info: t('Відгрузити'),
			requiresConfirmation: true,
			confirmText: t('Ви дійсно хочете відгрузити замовлення?'),
			disabled: (status) =>
				['rejected', 'shipped', 'received'].includes(status),
			handler: handlers.ship,
		},
		{
			type: 'info',
			icon: info,
			info: t('Інфо'),
			handler: handlers.info,
		},
	];

	const handleClick = (action: ActionConfig) => {
		if (action.requiresConfirmation) {
			setActiveConfirm(action.type);
		} else {
			action.handler(orderId);
		}
	};

	const handleConfirm = async () => {
		const action = actions.find((a) => a.type === activeConfirm);
		if (action) {
			try {
				await action.handler(orderId);
			} catch (err) {
				toast.error(t('Помилка'));
			}
		}
		setActiveConfirm(null);
	};
	return (
		<>
			{openInfoPopup && orderInfo && (
				<PopupStatus
					closePopup={handleClosePopup}
					order={adaptStoreOrderToPopupStatus(orderInfo)}
					removeButton={handlers.delete}
					editButton={handlers.edit}
					hidePrintButtons={true}
				/>
			)}
			{activeConfirm && (
				<ApproveAskPopup
					approvedCallback={handleConfirm}
					canceledCallback={() => setActiveConfirm(null)}
					questionText={
						actions.find((a) => a.type === activeConfirm)?.confirmText
					}
				/>
			)}
			{actions.map((action) => {
				const isDisabled =
					action.disabled?.(orderStatus, orderStatusPay) ?? false;
				return (
					<button
						key={action.type}
						title={action.info}
						disabled={isDisabled}
						type="button"
						onClick={() => handleClick(action)}
					>
						<img src={action.icon} alt={action.type} />
					</button>
				);
			})}
		</>
	);
};

export default CustomerOrderAction;
